import type { IntelligenceReport, IntelligenceUsage } from '../api/intelligenceReportSchemas';
import type { IntelligenceInsight, IntelligencePersona, IntelligenceSetupGap } from '../api/schemas';
import { formatMoneyCents } from './format';

/** Where a finding's button goes, with the filter it needs already applied. */
export type InsightLink = { to: string; search?: Record<string, string>; label: string };

const WEEKDAY_PLURAL = ['domingos', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábados'];
/** A link carries at most this many customer ids; the finding's own count still reads in full. */
const MAX_LINKED_CUSTOMERS = 100;
const MINUTE_MS = 60_000;
const HOUR_MIN = 60;
const DAY_MIN = 24 * HOUR_MIN;

const GAP_SCREEN_LINK: Record<string, { to: string; label: string }> = {
  servicios: { to: '/servicios', label: 'Ir a Servicios' },
  agenda: { to: '/agenda', label: 'Ir a Horario' },
  equipo: { to: '/equipo', label: 'Ir a Equipo' },
  negocio: { to: '/negocio', label: 'Ir a Negocio' },
  finanzas: { to: '/finanzas', label: 'Anotar gastos en Finanzas' },
};

/** The one button under a finding: it lands on the screen where the fix happens, already narrowed. */
export function insightLink(insight: IntelligenceInsight): InsightLink {
  const { action } = insight;
  const params: Record<string, unknown> = action.params;
  if ('gap' in params) return GAP_SCREEN_LINK[action.screen] ?? { to: '/', label: 'Ver' };
  if (action.screen === 'ofertas') {
    const weekday = Number(params.weekday);
    return { to: '/ofertas', search: { dias: String(weekday) }, label: `Crear oferta para los ${WEEKDAY_PLURAL[weekday] ?? 'días flojos'}` };
  }
  if (action.screen === 'clientes') {
    const ids = Array.isArray(params.customerIds) ? params.customerIds.map(String) : [];
    const label = ids.length === 1 ? 'Ver al cliente' : `Ver los ${ids.length} clientes`;
    return { to: '/clientes', search: { ids: ids.slice(0, MAX_LINKED_CUSTOMERS).join(',') }, label };
  }
  if (action.screen === 'agenda') return { to: '/calendario', label: 'Ver el calendario' };
  return { to: '/servicios', label: 'Revisar precios en Servicios' };
}

/** Setup gaps are split by area: an asesor owns only the gap kinds it lists. */
export function isOwnedBy(persona: IntelligencePersona, insight: IntelligenceInsight): boolean {
  if (!persona.insights.includes(insight.type)) return false;
  if (insight.type !== 'setup_gap') return true;
  const gap = setupGapOf(insight);
  return persona.gaps?.some((owned) => owned === gap) ?? false;
}

/**
 * Who signs a finding: the specialist who owns it, so each finding appears
 * once with one voice. Dirección oversees everything and signs only what no
 * specialist owns.
 */
export function signerOf(insight: IntelligenceInsight, personas: readonly IntelligencePersona[]): IntelligencePersona | undefined {
  const owners = personas.filter((persona) => isOwnedBy(persona, insight));
  return owners.find((persona) => persona.personaId !== 'direccion') ?? owners[0];
}

export function setupGapOf(insight: IntelligenceInsight): IntelligenceSetupGap | undefined {
  const gap = insight.metrics.gap;
  return typeof gap === 'string' ? (gap as IntelligenceSetupGap) : undefined;
}

/** "Actualizado hace 30 min": how fresh the numbers are, so nobody acts on a stale read. */
export function updatedLabel(generatedAt: string, now: Date): string {
  const generated = Date.parse(generatedAt);
  if (!Number.isFinite(generated)) return '';
  const minutes = Math.floor((now.getTime() - generated) / MINUTE_MS);
  if (minutes < 1) return 'Actualizado hace un momento';
  if (minutes < HOUR_MIN) return `Actualizado hace ${minutes} min`;
  if (minutes < DAY_MIN) return `Actualizado hace ${Math.floor(minutes / HOUR_MIN)} h`;
  const date = new Date(generated).toLocaleDateString('es-PA', { day: 'numeric', month: 'long' });
  return `Actualizado el ${date}`;
}

const PANAMA_TIME_ZONE = 'America/Panama';
const DAY_MS = 24 * 60 * MINUTE_MS;
/** What the engine reads when a report carries no usable range. */
const DEFAULT_REVIEW_DAYS = 90;

const panamaDay = (date: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: PANAMA_TIME_ZONE }).format(date);

/** "Hoy", "Ayer" or "12 de septiembre": when a report was written, on the Panama calendar. */
export function reportDateLabel(createdAt: string, now: Date): string {
  const created = Date.parse(createdAt);
  if (!Number.isFinite(created)) return '';
  const day = panamaDay(new Date(created));
  if (day === panamaDay(now)) return 'Hoy';
  if (day === panamaDay(new Date(now.getTime() - DAY_MS))) return 'Ayer';
  return new Date(created).toLocaleDateString('es-PA', { day: 'numeric', month: 'long', timeZone: PANAMA_TIME_ZONE });
}

const REVIEWED_NOUNS: readonly (readonly [keyof IntelligenceReport['reviewed'], string, string])[] = [
  ['bookings', 'reserva', 'reservas'],
  ['charges', 'cobro', 'cobros'],
  ['expenses', 'gasto', 'gastos'],
  ['customers', 'cliente', 'clientes'],
];

/** The range is inclusive on both ends, so 2 Jul to 29 Sep counts 90 days. */
function reviewedDays(range: IntelligenceReport['range']): number {
  const days = Math.round((Date.parse(range.to) - Date.parse(range.from)) / DAY_MS) + 1;
  return Number.isFinite(days) && days > 0 ? days : DEFAULT_REVIEW_DAYS;
}

/** "Revisó 312 reservas, 280 cobros, 14 gastos y 95 clientes de los últimos 90 días." */
export function reviewedLine(reviewed: IntelligenceReport['reviewed'], range: IntelligenceReport['range']): string {
  const days = reviewedDays(range);
  const span = days === 1 ? 'del último día' : `de los últimos ${days} días`;
  const parts = REVIEWED_NOUNS.filter(([key]) => reviewed[key] > 0).map(
    ([key, one, many]) => `${reviewed[key]} ${reviewed[key] === 1 ? one : many}`,
  );
  if (parts.length === 0) return `No había movimiento ${days === 1 ? 'en el último día' : `en los últimos ${days} días`}.`;
  const last = parts[parts.length - 1];
  const list = parts.length === 1 ? last : `${parts.slice(0, -1).join(', ')} y ${last}`;
  return `Revisó ${list} ${span}.`;
}

/** "Comentarios escritos este mes: US$3.40 de US$20.00" */
export function usageLine(usage: IntelligenceUsage): string {
  return `Comentarios escritos este mes: US${formatMoneyCents(usage.usedCents)} de US${formatMoneyCents(usage.capCents)}`;
}
